import { useRef } from 'react';
import {
  Id,
  toast,
  ToastContent,
  ToastOptions,
  UpdateOptions,
} from 'react-toastify';

type ToastCall = (content: ToastContent, options?: ToastOptions) => Id;

const callOrUpdate = (
  toastIdRef: React.MutableRefObject<Id>,
  call: ToastCall,
  content: ToastContent,
  options?: ToastOptions,
) => {
  if (!toastIdRef.current || !toast.isActive(toastIdRef.current)) {
    toastIdRef.current = call(content, options);
  } else {
    toast.update(toastIdRef.current, {
      render: content,
      ...options,
    });
  }
};

export const useUniqueToast = (defaultOptions?: ToastOptions) => {
  const toastIdRef = useRef<Id>(defaultOptions?.toastId || '');

  return {
    /** vanilla */
    toast: (content: ToastContent, options?: ToastOptions) =>
      callOrUpdate(toastIdRef, toast, content, {
        ...defaultOptions,
        ...options,
      }),

    success: (content: ToastContent, options?: ToastOptions) =>
      callOrUpdate(toastIdRef, toast.success, content, {
        ...defaultOptions,
        ...options,
      }),

    error: (content: ToastContent, options?: ToastOptions) =>
      callOrUpdate(toastIdRef, toast.error, content, {
        ...defaultOptions,
        ...options,
      }),

    info: (content: ToastContent, options?: ToastOptions) =>
      callOrUpdate(toastIdRef, toast.info, content, {
        ...defaultOptions,
        ...options,
      }),

    warning: (content: ToastContent, options?: ToastOptions) =>
      callOrUpdate(toastIdRef, toast.warning, content, {
        ...defaultOptions,
        ...options,
      }),

    loading: (content: ToastContent, options?: ToastOptions) =>
      callOrUpdate(toastIdRef, toast.loading, content, {
        ...defaultOptions,
        ...options,
      }),

    update: (toastId: Id, options?: UpdateOptions) => {
      if (toastIdRef.current === toastId) {
        toast.update(toastIdRef.current, options);
      } else {
        callOrUpdate(toastIdRef, toast, options?.render || '', {
          ...defaultOptions,
          ...options,
        } as ToastOptions);
      }
    },

    dismiss: () => {
      if (toastIdRef.current) {
        toast.dismiss(toastIdRef.current);
        toastIdRef.current = '';
      }
    },
  };
};
